import styles from "./product-page.module.scss";
import cl from "classnames";
import { observer } from "mobx-react-lite";
import { PageContainer } from "components";
import { useRootStore } from "mobx-stores/store-provider";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  AttributeGroupDTO,
  AttributeGroupValueDTO,
  Button,
  ImagePortrait,
  RatingStars as RatingStarsDisplay,
} from "@touche-lts/panel-ui/dist";
import { ImageGallery } from "components/general/image-gallery";
import { RatingStars } from "components/common";
import { ReviewDTO } from "models/review";
import { User } from "services";
import { IconArrowDropdownDown } from "assets/icons";

const REVIEWS_PER_PAGE = 3;

const ProductPageComponent = () => {
  const defaultImageSrc =
      "https://st2.depositphotos.com/4403291/7418/v/600/depositphotos_74189661-stock-illustration-online-shop-log.jpg",
    { productId } = useParams<{ productId: string }>(),
    {
      storesPublic: {
        getProductById,
        singlePageStore,
        productInfo,
        changeSelectedAttribute,
        addToCartProduct,
        changeSelectedShipping,
        setSkuByDefault,
        selectedSKU,
        selectedSkuStock,
        relatedImages,
        submitProductReview,
      },
      routingStore: { push },
    } = useRootStore(),
    userDetailsId = JSON.parse(localStorage.getItem("userDetails"))?.id,
    userRole = JSON.parse(localStorage.getItem("userDetails"))?.role?.name,
    isStoreAdmin = userRole === "STORE_ADMIN" || userRole === "SUPER_ADMIN",
    canSubmitReview =
      User.isAuthenticated && !isStoreAdmin,
    existingUserReview = productInfo?.reviews?.find(
      (review: ReviewDTO) => review.userId === userDetailsId,
    ),
    formRef = useRef<HTMLFormElement>(),
    handleSelectedValues = (): void => {
      addToCartProduct(userDetailsId, productCount);
    },
    handleBuyNow = (): void => {
      setBtnDisabled(true);
      addToCartProduct(userDetailsId, productCount, true);
    },
    getSelectedValue = (ev, index): void => {
      if (!attrIds.includes(ev.target.id)) {
        setAttrIds([...attrIds, ev.target.id]);
      }
      changeSelectedAttribute(ev.target.value, index);
      setProductCount(1);
    },
    countProductCount = (minusPlusCount: number): void => {
      const numberCount = productCount + minusPlusCount;
      if (!!numberCount) {
        setProductCount(numberCount);
      }
    },
    dropDownIndicatorStyle = {
      dropdownIndicator: (base) => ({
        ...base,
        color: "white",
        marginRight: 10,
      }),
    },
    [productCount, setProductCount] = useState(1),
    [attrIds, setAttrIds] = useState([]),
    [btnDisabled, setBtnDisabled] = useState(false),
    [reviewRating, setReviewRating] = useState<number | null>(null),
    [reviewText, setReviewText] = useState(""),
    [isSubmittingReview, setIsSubmittingReview] = useState(false),
    [reviewPage, setReviewPage] = useState(0),
    validateQty = (): boolean => {
      if (
        productInfo?.attGroups.length !== attrIds.length ||
        (productInfo?.productSkus.length === 1
          ? productInfo?.mainSku?.stock
          : selectedSkuStock) <= 1
      ) {
        return true;
      }
      return false;
    },
    handleSubmitReview = async (): Promise<void> => {
      if (!reviewRating || !reviewText.trim()) {
        return;
      }

      setIsSubmittingReview(true);
      const success = await submitProductReview(productId, {
        productId: Number(productId),
        rating: reviewRating,
        review: reviewText.trim(),
        userId: userDetailsId ?? User.id,
        userName: User.fullName.trim() || User.emailAddress,
      });

      if (success) {
        setReviewRating(null);
        setReviewText("");
      }
      setIsSubmittingReview(false);
    };

  const isNotValidQty = validateQty();
  const selectedSkuPrice = productInfo?.productSkus?.find(
    (sku) => sku.sku === selectedSKU,
  )?.price;
  const reviews = productInfo?.reviews ?? [];
  const totalReviewPages = Math.ceil(reviews.length / REVIEWS_PER_PAGE);
  const visibleReviews = reviews.slice(
    reviewPage * REVIEWS_PER_PAGE,
    reviewPage * REVIEWS_PER_PAGE + REVIEWS_PER_PAGE,
  );
  const isFirstReviewPage = reviewPage === 0;
  const isLastReviewPage =
    reviews.length === 0 || reviewPage >= totalReviewPages - 1;

  useEffect(() => {
    getProductById(productId);
    setProductCount(1);
    setAttrIds([]);
    formRef.current.reset();
  }, [getProductById, productId]);

  useEffect(() => {
    setSkuByDefault();
  }, [productInfo?.attGroups, setSkuByDefault]);

  useEffect(() => {
    setReviewPage(0);
  }, [productInfo?.id]);

  return (
    <PageContainer
      className={cl(styles.scrollable)}
      style={{ maxHeight: "fit-content" }}
    >
      <div className="container">
        <div className="row">
          <div className="col-lg-4">
            {relatedImages?.length > 0 && (
              <div className={styles.pictureGalleryContainer}>
                <ImageGallery images={relatedImages} />
              </div>
            )}
            {/* {!selectedSliderImages?.length && productInfo?.images && !!productInfo?.images.length && (
                    <div className={styles.pictureGalleryContainer}>
                        <ImageGallery
                            images={productInfo?.images}
                        />
                    </div>
                )}
                {!!selectedSliderImages?.length &&
                    <ImageGallery
                        images={selectedSliderImages}
                    />
                } */}
          </div>
          <div className="col-lg-8">
            <div className={styles.infoContainer}>
              <div className={styles.testInfo}>
                <h2>{productInfo?.name}</h2>
                <span>by {productInfo?.manufacturer}</span>
                <RatingStarsDisplay
                  rating={productInfo?.rating}
                  reviewsCnt={productInfo?.reviewsCnt}
                />
                <p>{productInfo?.info}</p>
              </div>
              <div className={styles.quantityPrice}>
                <h1>
                  US ${selectedSkuPrice ?? productInfo?.mainSku?.price}{" "}
                  {/** TODO-A Currency to be correctly fetch */}
                </h1>
                <div className={styles.quantityCount}>
                  <p className={styles.quantityText}>Quantity</p>
                  <span
                    className={
                      isNotValidQty || productCount === 1
                        ? styles.disableBehavior
                        : ""
                    }
                    onClick={() => countProductCount(-1)}
                  >
                    -
                  </span>
                  <p className={styles.quantity}>{productCount}</p>
                  <span
                    className={
                      isNotValidQty || productCount === selectedSkuStock
                        ? styles.disableBehavior
                        : ""
                    }
                    onClick={() => countProductCount(1)}
                  >
                    +
                  </span>
                </div>
              </div>
              {productCount === selectedSkuStock && (
                <div className={styles.quantityError}>
                  Maximum quantity available
                </div>
              )}
              {selectedSkuStock === 0 && (
                <div className={styles.quantityError}>Out of stock</div>
              )}

              <div className={styles.productAttributes}>
                <form ref={formRef}>
                  {productInfo?.attGroups?.map(
                    (attrGroup: AttributeGroupDTO, index) => {
                      return (
                        <div className={styles.productAttribute}>
                          <p>{attrGroup?.name}</p>
                          <select
                            id={String(attrGroup?.id)}
                            onChange={(ev) => getSelectedValue(ev, index)}
                            className={cl(
                              styles.selectShipping,
                              dropDownIndicatorStyle,
                            )}
                          >
                            {attrGroup?.values.map(
                              (attr: AttributeGroupValueDTO) => {
                                return (
                                  <>
                                    <option defaultValue={""} hidden>
                                      {"--"}
                                    </option>
                                    <option value={attr?.id}>
                                      {attr?.name}
                                    </option>
                                  </>
                                );
                              },
                            )}
                          </select>
                        </div>
                      );
                    },
                  )}
                </form>
              </div>
              {/* {!selectedSKU && (
                            <div className={styles.skuProduct}>SKU: {productInfo?.mainSku?.sku}</div>
                        )} */}
              {selectedSKU &&
                productInfo.attGroups.length === attrIds.length && (
                  <div className={styles.skuProduct}>SKU: {selectedSKU}</div>
                )}
              <hr className={styles.separateEndLine} />
              {productInfo?.shippings && (
                <div className={styles.productAttributes}>
                  <div className={styles.productAttribute}>
                    <p>Shippings</p>
                    <select
                      onChange={(ev) => changeSelectedShipping(ev)}
                      className={cl(
                        styles.selectShipping,
                        dropDownIndicatorStyle,
                      )}
                    >
                      {productInfo?.shippings.map((shipping, indexShipping) => {
                        return (
                          <>
                            <option value={shipping?.id}>
                              {shipping?.name} - {shipping?.currency}{" "}
                              {shipping?.price}
                            </option>
                          </>
                        );
                      })}
                    </select>
                  </div>
                </div>
              )}
              {!isStoreAdmin &&
                productInfo?.attGroups.length === attrIds.length &&
                selectedSkuStock !== 0 && (
                  <div className={styles.addToCartBuyNow}>
                    <Button
                      preset="secondary"
                      colorPreset="dark"
                      onClick={() => handleSelectedValues()}
                    >
                      Add to Cart
                    </Button>
                    <Button
                      preset="primary"
                      onClick={handleBuyNow}
                      disabled={btnDisabled}
                    >
                      Buy now
                    </Button>
                  </div>
                )}
            </div>
          </div>
        </div>
        <div className={styles.moreFromStoreName}>
          <p className={styles.moreFromStore}>
            More items from {productInfo?.store?.name}
          </p>
          <div className={cl(styles.storesContainer)}>
            {singlePageStore &&
              singlePageStore.content.slice(0, 5).map((product) => {
                // TODO-A To add model type
                return (
                  <div
                    onClick={() => push(`/product/${product?.id}`)}
                    className={styles.productInfo}
                  >
                    <div className={styles.productInfoDetails}>
                      <ImagePortrait
                        width={176}
                        height={176}
                        src={
                          !!product?.image
                            ? product?.image?.url
                            : defaultImageSrc
                        }
                      />
                      <div className={styles.productInfoDetailsText}>
                        <h2>{product.name}</h2>
                        <RatingStarsDisplay
                          rating={product?.rating}
                          reviewsCnt={product?.reviewsCnt}
                        />
                        <small>Add to cart</small>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <p
            onClick={() => push(`/stores/${productInfo?.storeId}`)}
            className={styles.goToStore}
          >
            Go to store
          </p>
          <hr className={styles.separateEndLine} />
          <p className={styles.moreFromStore}>
            Customer reviews {productInfo?.store?.name} (
            {productInfo?.reviews?.length})
          </p>
          <div className={styles.reviewsNavigation}>
            <Button
              className={cl(styles.reviewPrevButton)}
              onClick={() => setReviewPage((page) => Math.max(0, page - 1))}
              disabled={isFirstReviewPage}
            >
              <IconArrowDropdownDown />
            </Button>
            <div className={styles.reviewsContainer}>
              {visibleReviews.map((review: ReviewDTO) => {
                const reviewContent = review.review?.trim();
                return (
                  <div key={review.id} className="d-flex px-1">
                    <div className={styles.reviewsBox}>
                      <RatingStarsDisplay rating={review?.rating} />
                      <p>{review.userName}</p>
                      {reviewContent && (
                        <p className={styles.reviewText}>{reviewContent}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              className={cl(styles.reviewNextButton)}
              onClick={() =>
                setReviewPage((page) =>
                  Math.min(totalReviewPages - 1, page + 1),
                )
              }
              disabled={isLastReviewPage}
            >
              <IconArrowDropdownDown />
            </Button>
          </div>
          {canSubmitReview && !existingUserReview && (
            <div className={styles.reviewForm}>
              <p className={styles.reviewFormTitle}>Write a review</p>
              <RatingStars
                selfRating={true}
                rating={reviewRating ?? 0}
                rate={(rate: number) => setReviewRating(rate)}
                isAdmin={isStoreAdmin}
              />
              <textarea
                className={styles.reviewTextarea}
                placeholder="Share your experience with this product..."
                value={reviewText}
                onChange={(ev) => setReviewText(ev.target.value)}
                rows={4}
              />
              <Button
                className={styles.submitReviewBtn}
                preset="primary"
                onClick={handleSubmitReview}
                disabled={
                  isSubmittingReview || !reviewRating || !reviewText.trim()
                }
              >
                Submit review
              </Button>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

const ProductPage = observer(ProductPageComponent);
export { ProductPage };
